import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Check, ChevronRight, Minus, Pencil, Plus, TriangleAlert, X } from 'lucide-react';
import { REGRA_BONIFICACAO as R, calcularBonificacao, faixaDe, segmentoDe } from '../../../src/lib/bonificacaoParceiro';
import EntendaConta from './EntendaConta';
import type { Cliente } from './EntendaConta';
import { kwh, lerConsumo, mil, pc, pc2, reais } from './formato';

// Simulador de bonificação do parceiro, no Academy: o mesmo da página de parceiros
// do site (vigorenergy.com.br/parceiros), com as cores do VigorHub. A conta é a de
// src/lib/bonificacaoParceiro.ts (regra de 26/10/2026). Mostra só como a regra é
// hoje: nada de "no plano padrão seria".

type Modalidade = 'fixa' | 'variavel';
type Aviso = { tx: string; desfazer?: { item: Cliente; pos: number } };

const CONSUMO_MAX = 9_999_999;
const FX = R.faixas;
const NF = FX.length;

// 1ª visita: a régua já aparece com a conta funcionando (marcado como exemplo; sai com um toque)
const EXEMPLO = [
  { c: 650, d: 20, nome: 'Residência' },
  { c: 2300, d: 20, nome: 'Padaria' },
  { c: 7800, d: 26, nome: 'Supermercado' },
];
let seq = 0;
const novoId = () => `c${++seq}`;
const exemplo = (): Cliente[] => EXEMPLO.map(x => ({ ...x, id: novoId(), exemplo: true }));
const formVazio = { consumo: '', d: R.padrao as number, nome: '' };

/** Posição na régua (0 a 1): cada faixa ocupa 1/6; dentro dela, a parte já percorrida. */
function posicaoRegua(v: number): number {
  if (!(v > 0)) return 0;
  const f = faixaDe(v)!;
  const lo = f.i ? FX[f.i - 1][0] : 0;
  const hi = FX[f.i][0];
  const dentro = hi === Infinity ? (v - lo) / lo : (v - lo) / (hi - lo);
  return (f.i + Math.max(0, Math.min(1, dentro))) / NF;
}

// Destaque de quem acabou de entrar na régua (o mesmo do site).
const ANIMACOES = `
@keyframes sim-regua-entra { from { background-color: #F6A937; } }
@keyframes sim-ficha-entra { from { border-color: #F6A937; background-color: rgb(246 169 55 / 0.18); } }
@media (prefers-reduced-motion: reduce) { .sim-anima { animation: none !important; } }
`;
const EASE_SAIDA = 'ease-[cubic-bezier(0.23,1,0.32,1)]';
const EASE_MOVIMENTO = 'ease-[cubic-bezier(0.77,0,0.175,1)]';

const SimuladorBonificacao: React.FC = () => {
  const [clientes, setClientes] = useState<Cliente[]>(exemplo);
  const [tipo, setTipo] = useState<Modalidade>('fixa');
  const [form, setForm] = useState(formVazio);
  const [editando, setEditando] = useState<string | null>(null);
  const [realce, setRealce] = useState<string | null>(null);
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const [trilhoW, setTrilhoW] = useState(600);
  const [anuncio, setAnuncio] = useState('');
  const trilhoRef = useRef<HTMLDivElement>(null);
  const consumoRef = useRef<HTMLInputElement>(null);
  const fixo = tipo === 'fixa';

  const res = useMemo(() => calcularBonificacao(clientes), [clientes]);
  const fx = res.fx;
  const rotulo = (x: Cliente, i: number) => x.nome.trim() || `Consumidor ${i + 1}`;
  const temExemplo = clientes.some(x => x.exemplo);

  // o consumidor do formulário, já somado ao mês, para a prévia e o bloco tracejado na régua
  const c = lerConsumo(form.consumo);
  const sg = segmentoDe(c);
  const iEditando = editando ? clientes.findIndex(x => x.id === editando) : -1;
  const idxForm = iEditando >= 0 ? iEditando : clientes.length;
  const res1 = useMemo(() => {
    if (!Number.isFinite(c)) return null;
    const lista = clientes.map(x => ({ c: x.c, d: x.d }));
    lista[idxForm] = { c, d: form.d };
    return calcularBonificacao(lista);
  }, [clientes, c, form.d, idxForm]);
  const l1 = res1?.linhas[idxForm];

  let bloqueio = '';
  const lotado = !editando && clientes.length >= R.maxPorMes;
  if (lotado) bloqueio = `A simulação já tem ${R.maxPorMes} consumidores.`;
  else if (form.consumo.trim() && !Number.isFinite(c)) bloqueio = 'Use só números no consumo.';
  else if (c > CONSUMO_MAX) bloqueio = `Consumo acima de ${kwh(CONSUMO_MAX)} kWh por mês.`;
  else if (c < R.minimo) bloqueio = `Abaixo de ${kwh(R.minimo)} kWh por mês: o consumidor não entra na conta.`;
  else if (sg && form.d > sg.max) bloqueio = `No segmento ${sg.n}, o desconto vai até ${sg.max}%.`;
  const podeAdicionar = !bloqueio && Number.isFinite(c) && !!l1?.ok;

  // largura real do trilho, para decidir quanto texto cabe em cada bloco
  useEffect(() => {
    const el = trilhoRef.current;
    if (!el || !('ResizeObserver' in window)) return;
    const ro = new ResizeObserver(([e]) => setTrilhoW(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!realce) return;
    const t = setTimeout(() => setRealce(null), 1400);
    return () => clearTimeout(t);
  }, [realce]);

  // leitor de tela: um resumo depois que a pessoa para de mexer
  useEffect(() => {
    const t = setTimeout(() => {
      setAnuncio(
        fx
          ? `${fixo ? 'Fixa' : 'Variável'}. Faixa F${fx.i + 1}, ${pc(fx.pct)} da fatura com desconto. ` +
              (fixo ? `Bonificação do mês ${reais(res.B)}. Pix I ${reais(res.E)}. Pix II ${reais(res.S)}.` : `Pix I ${reais(res.E)}. Recorrente ${reais(res.P)} por mês.`)
          : 'Nenhum consumidor na simulação.',
      );
    }, 800);
    return () => clearTimeout(t);
  }, [fx, fixo, res.B, res.E, res.S, res.P]);

  function salvar(e: FormEvent) {
    e.preventDefault();
    if (!podeAdicionar) return;
    const nome = form.nome.trim().slice(0, 60);
    if (editando) {
      setClientes(cs => cs.map(x => (x.id === editando ? { ...x, c, d: form.d, nome, exemplo: false } : x)));
      setRealce(editando);
      setAviso({ tx: 'Alteração salva.' });
      setEditando(null);
    } else {
      const id = novoId();
      setClientes(cs => [...cs, { id, c, d: form.d, nome }]);
      setRealce(id);
      setAviso({ tx: `${nome || `Consumidor ${clientes.length + 1}`} entrou na régua.` });
    }
    setForm(formVazio);
    consumoRef.current?.focus();
  }

  function editar(i: number) {
    const x = clientes[i];
    setEditando(x.id);
    setForm({ consumo: kwh(x.c), d: x.d, nome: x.nome });
    setAviso(null);
    consumoRef.current?.focus({ preventScroll: true });
    consumoRef.current?.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  function remover(i: number) {
    const item = clientes[i];
    setClientes(cs => cs.filter(x => x.id !== item.id));
    if (editando === item.id) cancelarEdicao();
    setAviso({ tx: `${rotulo(item, i)} saiu da régua.`, desfazer: { item, pos: i } });
  }

  function desfazer() {
    const d = aviso?.desfazer;
    if (!d) return;
    setClientes(cs => [...cs.slice(0, d.pos), d.item, ...cs.slice(d.pos)]);
    setRealce(d.item.id);
    setAviso(null);
  }

  function cancelarEdicao() {
    setEditando(null);
    setForm(formVazio);
  }

  function limparExemplo() {
    if (editando && clientes.find(x => x.id === editando)?.exemplo) cancelarEdicao();
    setClientes(cs => cs.filter(x => !x.exemplo));
    setAviso({ tx: 'Exemplo removido. Adicione os seus consumidores.' });
    consumoRef.current?.focus();
  }

  function verExemplo() {
    setClientes(exemplo());
    setAviso(null);
  }

  // texto do topo da régua: quanto falta para a próxima faixa
  const naUltima = !!fx && FX[fx.i][0] === Infinity;
  const falta = fx && !naUltima ? FX[fx.i][0] + 1 - res.V : 0;
  const faltaPouco = !!fx && !naUltima && falta < R.minimo;

  // blocos da régua: um por consumidor que entra na conta, na ordem da lista
  let acumulado = 0;
  let numero = 0;
  const blocos = res.linhas.map((l, i) => {
    if (!l.ok) return null;
    numero++;
    const a = posicaoRegua(acumulado);
    const b = posicaoRegua(acumulado + l.c);
    acumulado += l.c;
    const x = clientes[i];
    return { id: x.id, n: numero, nome: rotulo(x, i), c: l.c, a, b, px: (b - a) * trilhoW };
  });
  const fantasma = !editando && res1 && podeAdicionar && res1.V > res.V ? { a: posicaoRegua(res.V), b: posicaoRegua(res1.V) } : null;

  const nOk = res.linhas.filter(l => l.ok).length;
  const nEx = clientes.filter(x => x.exemplo).length;
  const sub = fx
    ? `Faixa F${fx.i + 1} (${pc(fx.pct)} da fatura com desconto) · ${nOk} ${nOk > 1 ? 'consumidores' : 'consumidor'}` +
      (nEx === nOk ? ' de exemplo' : nEx ? ` (${nEx} de exemplo)` : '')
    : 'Adicione um consumidor para ver a sua bonificação.';

  // prévia do formulário: o que o consumidor rende e como fica o mês com ele
  let efeito = '';
  if (res1 && l1?.ok && podeAdicionar && res1.fx) {
    const v1 = fixo ? res1.B : res1.E;
    const oq = fixo ? 'a bonificação do mês' : 'o Pix I do mês';
    efeito = `Com ${editando ? 'a alteração' : 'ele'}, ${oq} fica em ${reais(v1)}, na faixa F${res1.fx.i + 1} (${pc(res1.fx.pct)}).`;
  }

  let dica = `Mínimo de ${kwh(R.minimo)} kWh por mês. Desconto padrão de ${R.padrao}%.`;
  if (sg)
    dica =
      `Segmento ${sg.n}: ${sg.ate === Infinity ? `${kwh(sg.de)} kWh ou mais` : `${kwh(sg.de)} a ${kwh(sg.ate)} kWh`}, ` +
      `desconto de ${R.padrao}% a ${sg.max}%. Acima de ${R.autoriza}%, só com aprovação da Vigor.`;
  const dMax = sg ? sg.max : R.maximo;

  const btIcone = 'grid place-items-center w-9 h-9 rounded-lg text-white/65 hover:text-white hover:bg-white/10 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-secondary';
  const btMaisMenos = `grid place-items-center w-11 h-12 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 hover:border-primary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-200 transition-[border-color,transform] duration-150 ${EASE_SAIDA} active:enabled:scale-[0.97]`;

  return (
    <section className="scroll-mt-8" aria-labelledby="titulo-calculadora">
      <style>{ANIMACOES}</style>
      <div className="text-center mb-8">
        <h2 id="titulo-calculadora" className="text-3xl md:text-4xl font-bold font-poppins text-gray-900 dark:text-white mb-3 tracking-tight">
          Simule sua bonificação
        </h2>
        <p className="text-base md:text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
          Some os consumidores que você fecharia em um mês. Quanto mais kWh no mês, maior a faixa, e ela vale para todos eles.
        </p>
      </div>

      <div className="max-w-5xl mx-auto">
        <div className="bg-primary-dark text-white rounded-3xl overflow-hidden shadow-xl shadow-primary-dark/20">
          <div className="p-5 sm:p-8 md:p-10 grid grid-cols-[minmax(0,1fr)] gap-10">
            {/* total do mês e modalidade */}
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-white/75">{fixo ? 'Bonificação do mês' : 'Pix I + recorrente'}</h3>
                <p className={`mt-1 text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight tabular-nums leading-[1.05] ${fx ? '' : 'text-white/45'}`}>
                  {fx ? reais(fixo ? res.B : res.E) : reais(0)}
                  {fx && !fixo ? (
                    <span className="block sm:inline sm:ml-3 mt-1 sm:mt-0 text-xl sm:text-2xl font-semibold tracking-normal text-white/80">+ ≈ {reais(res.P)} por mês</span>
                  ) : null}
                </p>
                <p className="mt-2 text-sm sm:text-base text-white/75">{sub}</p>
              </div>
              <div className="inline-flex p-1 rounded-full bg-white/10" role="group" aria-label="Como você recebe">
                {(['fixa', 'variavel'] as const).map(t => (
                  <button
                    key={t}
                    type="button"
                    aria-pressed={tipo === t}
                    onClick={() => setTipo(t)}
                    className={`min-h-11 px-6 rounded-full text-sm font-bold transition-[background-color,color,transform] duration-150 ${EASE_SAIDA} active:scale-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary ${
                      tipo === t ? 'bg-secondary text-gray-900' : 'text-white/80 hover:text-white'
                    }`}
                  >
                    {t === 'fixa' ? 'Fixa' : 'Variável'}
                  </button>
                ))}
              </div>
            </div>

            {/* régua do mês: a escada das faixas em cima, os consumidores enchendo o trilho embaixo */}
            <div>
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <h3 className="font-semibold">Régua do mês</h3>
                {fx ? (
                  <p className="text-sm text-white/75 tabular-nums">
                    {kwh(res.V)} kWh no mês ·{' '}
                    {naUltima ? (
                      <b className="text-secondary">você está na maior faixa</b>
                    ) : (
                      <>
                        faltam <b className="text-secondary">{kwh(falta)} kWh</b> para {pc(FX[fx.i + 1][1])}
                        {faltaPouco ? ', basta mais um consumidor' : ''}
                      </>
                    )}
                  </p>
                ) : null}
              </div>

              <div
                className="mt-5"
                role="img"
                aria-label={`Régua do mês: ${kwh(res.V)} kWh somados${fx ? `, faixa F${fx.i + 1} com ${pc(fx.pct)}` : ''}. Faixas: ${FX.map((f, i) => `F${i + 1} ${pc(f[1])}`).join(', ')}.`}
              >
                <div className="grid grid-cols-6 gap-1.5 sm:gap-2 items-end h-28 mb-1.5">
                  {FX.map((f, i) => {
                    const atual = fx?.i === i;
                    const passou = !!fx && i < fx.i;
                    return (
                      <div key={i} className="h-full flex flex-col justify-end items-center gap-0.5">
                        <span className={`text-sm sm:text-base font-bold tabular-nums ${atual ? 'text-white' : 'text-white/75'}`}>{pc(f[1])}</span>
                        <span className={`text-[11px] font-semibold mb-1 ${atual ? 'text-secondary' : 'text-white/60'}`}>F{i + 1}</span>
                        <i
                          className={`block w-full max-w-[72px] rounded-t-md transition-colors duration-300 ${atual ? 'bg-secondary' : passou ? 'bg-primary-light/45' : 'bg-white/10'}`}
                          style={{ height: `${(f[1] / FX[NF - 1][1]) * 62}%`, minHeight: 6 }}
                        />
                      </div>
                    );
                  })}
                </div>

                <div ref={trilhoRef} className="relative h-9 rounded-xl bg-white/10">
                  {FX.slice(1).map((_, s) => (
                    <i key={s} className="absolute inset-y-0 w-px bg-white/15" style={{ left: `${((s + 1) / NF) * 100}%` }} />
                  ))}
                  {blocos.map(b =>
                    b ? (
                      <i
                        key={b.id}
                        title={`${b.n}. ${b.nome} · ${kwh(b.c)} kWh`}
                        className={`sim-anima absolute inset-y-0 flex items-center px-2 overflow-hidden rounded-lg bg-primary-light text-primary-dark text-xs font-bold not-italic whitespace-nowrap shadow-[inset_-2px_0_0_#212913] transition-[left,width] duration-[420ms] ${EASE_MOVIMENTO} motion-reduce:transition-none`}
                        style={{ left: `${b.a * 100}%`, width: `${(b.b - b.a) * 100}%`, animation: realce === b.id ? 'sim-regua-entra 1.4s cubic-bezier(0.23,1,0.32,1)' : undefined }}
                      >
                        <span className="truncate">{b.px >= 84 ? `${b.n} · ${b.nome}` : b.px >= 18 ? b.n : ''}</span>
                      </i>
                    ) : null,
                  )}
                  {fantasma ? (
                    <i
                      className="absolute inset-y-0 flex items-center px-2 overflow-hidden rounded-lg bg-secondary/20 border-2 border-dashed border-secondary text-secondary text-xs font-bold not-italic whitespace-nowrap"
                      style={{ left: `${fantasma.a * 100}%`, width: `${(fantasma.b - fantasma.a) * 100}%` }}
                    >
                      {(fantasma.b - fantasma.a) * trilhoW >= 48 ? '+ novo' : ''}
                    </i>
                  ) : null}
                  {res.V > 0 ? (
                    <i
                      className={`absolute -top-1.5 -bottom-1.5 w-1 -ml-0.5 rounded-sm bg-secondary shadow-[0_0_0_2px_#212913] transition-[left] duration-[420ms] ${EASE_MOVIMENTO} motion-reduce:transition-none`}
                      style={{ left: `${posicaoRegua(res.V) * 100}%` }}
                    />
                  ) : null}
                </div>
                <div className="relative h-5 mt-1.5 text-[11px] text-white/60 tabular-nums" aria-hidden="true">
                  {FX.slice(0, -1).map((f, i) => (
                    <span key={i} className="absolute top-0 -translate-x-1/2 whitespace-nowrap" style={{ left: `${((i + 1) / NF) * 100}%` }}>
                      {mil(f[0] === 39999 ? 40000 : f[0])}
                    </span>
                  ))}
                </div>
              </div>
              <p className="mt-1 text-sm text-white/65">Cada bloco é um consumidor. A faixa vem da soma do mês, e o percentual dela vale para todos.</p>

              <ul className="mt-5 flex flex-wrap gap-2 items-center">
                {clientes.length === 0 ? (
                  <li className="flex flex-wrap items-center gap-3 text-sm text-white/80">
                    Nenhum consumidor ainda. Adicione o primeiro abaixo.
                    <button type="button" onClick={verExemplo} className="font-semibold text-secondary underline underline-offset-4 decoration-1 hover:text-white">
                      Ver um exemplo
                    </button>
                  </li>
                ) : null}
                {clientes.map((x, i) => {
                  const l = res.linhas[i];
                  const nm = rotulo(x, i);
                  const n = blocos[i]?.n;
                  return (
                    <li
                      key={x.id}
                      className={`sim-anima w-full sm:w-auto max-w-full sm:max-w-[26rem] inline-flex items-center gap-2.5 rounded-xl border bg-white/[0.06] py-1.5 pl-1.5 pr-1 text-sm transition-colors duration-200 ${
                        editando === x.id ? 'border-secondary' : 'border-white/15'
                      }`}
                      style={{ animation: realce === x.id ? 'sim-ficha-entra 1.4s cubic-bezier(0.23,1,0.32,1)' : undefined }}
                    >
                      <span className="grid place-items-center min-w-6 h-6 px-1 rounded-lg bg-primary-light text-primary-dark text-xs font-bold shrink-0">{n ?? '–'}</span>
                      <span className="grid leading-tight min-w-0 flex-1 sm:flex-initial">
                        <span className="flex items-baseline justify-between gap-3 min-w-0">
                          <b className="font-semibold truncate" title={nm}>{nm}</b>
                          {l.ok && fx ? <span className="font-semibold tabular-nums whitespace-nowrap">{reais(fixo ? l.B : l.E)}</span> : null}
                        </span>
                        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-white/65 tabular-nums">
                          <span className="whitespace-nowrap">{kwh(x.c)} kWh · {x.d}%</span>
                          {x.d > R.autoriza ? <span className="rounded-full bg-secondary/20 px-1.5 text-[11px] font-bold text-secondary">aprovação</span> : null}
                        </span>
                      </span>
                      <span className="flex shrink-0">
                        <button type="button" onClick={() => editar(i)} aria-label={`Editar ${nm}`} title="Editar" className={btIcone}>
                          <Pencil className="w-4 h-4" aria-hidden="true" />
                        </button>
                        <button type="button" onClick={() => remover(i)} aria-label={`Remover ${nm}`} title="Remover" className={btIcone}>
                          <X className="w-4 h-4" aria-hidden="true" />
                        </button>
                      </span>
                    </li>
                  );
                })}
                {temExemplo ? (
                  <li>
                    <button type="button" onClick={limparExemplo} className="px-2 py-2 text-sm font-semibold text-secondary underline underline-offset-4 decoration-1 hover:text-white">
                      Limpar exemplo
                    </button>
                  </li>
                ) : null}
              </ul>
            </div>

            {/* você recebe: as moedas no tempo */}
            <div>
              <h3 className="font-semibold">Você recebe</h3>
              {!fx ? (
                <p className="mt-3 text-sm text-white/75">Adicione um consumidor para ver quanto e quando.</p>
              ) : (
                <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                  <div className="flex items-center gap-3 min-w-0">
                    <i className="w-7 h-7 rounded-full bg-secondary shadow-[inset_0_0_0_3px_rgb(138_90_15/0.3)] shrink-0" aria-hidden="true" />
                    <div className="leading-tight">
                      <b className="block text-xl font-bold tabular-nums">{reais(res.E)}</b>
                      <span className="text-sm text-white/70">Pix I ({pc(R.entrada)}) · dia 10 do mês seguinte ao aceite</span>
                    </div>
                  </div>
                  <span aria-hidden="true" className="ml-3.5 sm:ml-0 flex items-center sm:flex-1 sm:min-w-10 text-white/25">
                    <i className="block w-0.5 h-5 sm:w-auto sm:h-0.5 sm:flex-1 bg-current" />
                    <ChevronRight className="hidden sm:block w-4 h-4 -ml-2.5 shrink-0" strokeWidth={2.5} />
                  </span>
                  {fixo ? (
                    <div className="flex items-center gap-3 min-w-0">
                      <i className="w-11 h-11 -ml-2 sm:ml-0 rounded-full bg-secondary shadow-[inset_0_0_0_4px_rgb(138_90_15/0.32)] shrink-0" aria-hidden="true" />
                      <div className="leading-tight">
                        <b className="block text-xl font-bold tabular-nums">{reais(res.S)}</b>
                        <span className="text-sm text-white/70">Pix II ({pc(1 - R.entrada)}) · dia 10 do mês seguinte à 1ª fatura paga</span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="inline-flex gap-1 shrink-0 -ml-2 sm:ml-0" aria-hidden="true">
                        {Array.from({ length: 7 }, (_, k) => (
                          <i key={k} className={`w-3 h-3 rounded-full bg-secondary ${k >= 3 ? 'hidden sm:block' : ''}`} style={{ opacity: 1 - k * 0.11 }} />
                        ))}
                      </span>
                      <div className="leading-tight">
                        <b className="block text-xl font-bold tabular-nums">{reais(res.P)} por mês</b>
                        <span className="text-sm text-white/70">recorrente estimado, nos meses em que o consumidor pagar, por {R.recorrenteAnos} anos depois da assinatura</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* adicionar consumidor: a faixa clara do mesmo painel */}
          <form onSubmit={salvar} className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 p-5 sm:p-8 md:px-10" aria-labelledby="sim-add">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 id="sim-add" className="font-bold text-lg tracking-tight">{editando ? 'Editar consumidor' : 'Adicionar consumidor'}</h3>
              {editando ? (
                <button type="button" onClick={cancelarEdicao} className="text-sm font-semibold text-primary dark:text-primary-light underline underline-offset-4 decoration-1 hover:text-primary-dark">
                  Cancelar edição
                </button>
              ) : null}
            </div>

            <div className="grid gap-4 grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,1.25fr)_auto_minmax(0,1fr)_auto] items-end">
              <label className="grid gap-1.5 min-w-0" htmlFor="sim-consumo">
                <span className="text-sm font-semibold">Consumo médio</span>
                <span className="flex items-center h-12 px-4 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/60 focus-within:bg-white dark:focus-within:bg-gray-900 focus-within:ring-2 focus-within:ring-primary/40 focus-within:border-primary transition-[background-color,border-color,box-shadow]">
                  <input
                    ref={consumoRef}
                    id="sim-consumo"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="ex.: 4.000"
                    aria-describedby="sim-dica sim-msg"
                    aria-invalid={!!bloqueio && !!form.consumo.trim()}
                    value={form.consumo}
                    onChange={e => {
                      const v = lerConsumo(e.target.value);
                      setForm(f => ({ ...f, consumo: Number.isFinite(v) ? kwh(v) : e.target.value.replace(/\d/g, '') ? e.target.value : '' }));
                    }}
                    className="flex-1 w-0 min-w-0 bg-transparent outline-none text-xl font-semibold tabular-nums text-gray-900 dark:text-white placeholder:text-gray-400 placeholder:font-normal caret-primary"
                  />
                  <span className="text-sm font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">kWh/mês</span>
                </span>
              </label>

              <div className="grid gap-1.5">
                <span className="text-sm font-semibold" id="sim-desc-rot">Desconto</span>
                <div className="flex items-center gap-1.5" role="group" aria-labelledby="sim-desc-rot">
                  <button type="button" aria-label="Diminuir o desconto" disabled={form.d <= R.padrao} onClick={() => setForm(f => ({ ...f, d: Math.max(R.padrao, f.d - 1) }))} className={btMaisMenos}>
                    <Minus className="w-4 h-4" aria-hidden="true" />
                  </button>
                  <output className="min-w-16 text-center text-xl font-semibold tabular-nums" aria-live="polite">{form.d}%</output>
                  <button type="button" aria-label="Aumentar o desconto" disabled={form.d >= dMax} onClick={() => setForm(f => ({ ...f, d: Math.min(dMax, f.d + 1) }))} className={btMaisMenos}>
                    <Plus className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
              </div>

              <label className="grid gap-1.5 min-w-0 sm:col-span-2 lg:col-span-1" htmlFor="sim-nome">
                <span className="text-sm font-semibold">
                  Nome <span className="font-normal text-gray-500 dark:text-gray-400">opcional</span>
                </span>
                <input
                  id="sim-nome"
                  autoComplete="off"
                  maxLength={60}
                  placeholder="ex.: Padaria Central"
                  value={form.nome}
                  onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                  className="w-full h-12 px-4 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/60 focus:bg-white dark:focus:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-[background-color,border-color,box-shadow] text-gray-900 dark:text-white placeholder:text-gray-400 caret-primary"
                />
              </label>

              <button
                type="submit"
                disabled={!podeAdicionar}
                className={`sm:col-span-2 lg:col-span-1 h-12 px-6 rounded-full bg-primary text-white font-bold inline-flex items-center justify-center gap-2 whitespace-nowrap hover:bg-primary-dark disabled:opacity-45 disabled:cursor-not-allowed transition-[background-color,transform] duration-150 ${EASE_SAIDA} active:enabled:scale-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary`}
              >
                {editando ? <Check className="w-5 h-5" aria-hidden="true" /> : <Plus className="w-5 h-5" aria-hidden="true" />}
                {editando ? 'Salvar alteração' : 'Adicionar'}
              </button>
            </div>

            <p id="sim-dica" className="mt-3 text-sm text-gray-500 dark:text-gray-400">{dica}</p>

            {podeAdicionar && l1 ? (
              <div className="mt-4 grid gap-2 text-sm text-gray-500 dark:text-gray-400">
                <p>
                  Este consumidor rende{' '}
                  {fixo ? (
                    <>
                      <b className="text-gray-900 dark:text-white tabular-nums">{reais(l1.B)}</b> (Pix I {reais(l1.E)} + Pix II {reais(l1.S)})
                    </>
                  ) : (
                    <>
                      Pix I <b className="text-gray-900 dark:text-white tabular-nums">{reais(l1.E)}</b> e depois <b className="text-gray-900 dark:text-white tabular-nums">{reais(l1.P)}</b> por fatura paga ({pc2(l1.P / l1.F)})
                    </>
                  )}
                  . {efeito}
                </p>
                {form.d > R.padrao ? (
                  <p className="inline-flex items-center gap-1.5 font-semibold text-secondary-dark dark:text-secondary">
                    <TriangleAlert className="w-4 h-4" aria-hidden="true" />
                    Condição especial: precisa de aprovação da Vigor
                  </p>
                ) : null}
              </div>
            ) : null}

            <p id="sim-msg" className="mt-3 text-sm min-h-5" aria-live="polite">
              {bloqueio && (form.consumo.trim() || lotado) ? (
                <span className="font-semibold text-red-700 dark:text-red-400">{bloqueio}</span>
              ) : aviso ? (
                <span className="text-gray-500 dark:text-gray-400">
                  {aviso.tx}{' '}
                  {aviso.desfazer ? (
                    <button type="button" onClick={desfazer} className="font-semibold text-primary dark:text-primary-light underline underline-offset-4 decoration-1 hover:text-primary-dark">
                      Desfazer
                    </button>
                  ) : null}
                </span>
              ) : null}
            </p>
          </form>
        </div>

        <EntendaConta res={res} clientes={clientes} fixo={fixo} />

        <p className="mt-8 text-sm text-gray-500 dark:text-gray-400 max-w-2xl mx-auto text-center">
          Simulação com valores brutos, antes de impostos e retenções. Não é garantia de pagamento: os valores finais seguem o Contrato de Parceria e
          dependem das faturas pagas pelos consumidores. Regra de bonificação de {R.vigencia}.
        </p>
      </div>
      <p className="sr-only" aria-live="polite">{anuncio}</p>
    </section>
  );
};

export default SimuladorBonificacao;
